import type { ProductData, ProductDataList } from '~~/shared-components/types/Product'
import type { GenericEnumObject } from '~~/shared-components/types/Objects'
import { locales } from '@@/shared-components/locales/index'

export const productTypesQuery = ({ query }: { query?: Ref<object> } = {}) =>
  queryOptions({
    queryKey: ['product-types', query],
    queryFn: async ({ signal }): Promise<GenericEnumObject[]> => {
      const { get } = useApi()
      const res = await get<GenericEnumObject[]>(
        '/enums/product-types',
        {
          ...query?.value,
        },
        { signal }
      )
      return res.data
    },
  })

export const publicProductQuery = ({
  id,
  query,
  thumbs,
  hasPreview,
  placeholderData,
  forceData = false,
  origin,
  dataLang,
  currency,
}: {
  id: string
  query?: object
  thumbs?: { [key: string]: string }
  hasPreview?: { expires: string; signature: string }
  placeholderData?: ProductData
  forceData?: boolean
  origin?: string
  dataLang: Ref<ValueOf<typeof locales>>
  currency: Ref<string>
}) =>
  queryOptions({
    queryKey: ['products', 'detail', id, dataLang, currency],
    queryFn: async (): Promise<ProductData> => {
      // Nefetchuje z BE pokud již data má ale queryClient není k dispozici (např. v middleweru)
      if (forceData && placeholderData) {
        return placeholderData
      }
      return useCustomFetch<ProductData>({
        url: `/public/products/${hasPreview ? 'p/' : ''}${id}`,
        origin,
        query: { ...query, ...(hasPreview ?? {}) },
        thumbs,
        options: { headers: { 'X-Lang': dataLang.value } },
        clientOnly: true,
      })
    },
    placeholderData,
  })

export const publicProductsQuery = ({
  enabled = shallowRef(true),
  query = shallowRef({}),
  pageNumber = shallowRef(1),
  thumbs,
  placeholderData,
  dataLang,
  currency,
}: {
  enabled?: Ref<boolean>
  query?: Ref<object>
  pageNumber?: Ref<number>
  thumbs?: { [key: string]: string }
  placeholderData?: ProductDataList
  dataLang: Ref<ValueOf<typeof locales>>
  currency: Ref<string>
}) =>
  queryOptions({
    enabled,
    queryKey: ['products', currency, dataLang, query],
    queryFn: async ({ signal }): Promise<ProductDataList> =>
      await useCustomFetch<ProductDataList>({
        url: '/public/products',
        query: {
          ...query?.value,
          page: {
            ...(query?.value && 'page' in query.value && typeof query.value?.page === 'object'
              ? query?.value.page
              : {}),
            number:
              pageNumber?.value ??
              (query?.value &&
              'page' in query?.value &&
              typeof query?.value?.page === 'object' &&
              query.value?.page &&
              'number' in query.value?.page
                ? query.value?.page.number
                : undefined),
          },
        },
        signal,
        options: {
          headers: { 'X-Lang': dataLang.value },
        },
        clientOnly: true,
        thumbs,
      }),

    placeholderData,
  })

export const publicInfiniteProductsQuery = ({
  query,
  thumbs,
  dataLang,
  currency,
}: {
  query?: Ref<object>
  thumbs?: { [key: string]: string }
  dataLang: Ref<ValueOf<typeof locales>>
  currency: Ref<string>
}) =>
  infiniteQueryOptions({
    queryKey: ['products', 'list', currency, dataLang, query],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      await useCustomFetch<ProductDataList>({
        url: '/public/products',
        query: {
          ...query?.value,
          page: {
            ...(query?.value && 'page' in query.value && typeof query.value.page === 'object'
              ? query.value.page
              : {}),
            number: pageParam,
          },
        },
        clientOnly: true,
        thumbs,
      }),
    getNextPageParam: (currentPage) => {
      if (!currentPage.meta.hasMorePages) {
        return undefined
      }
      return currentPage.meta.currentPage + 1
    },
    getPreviousPageParam: (currentPage) => {
      if (currentPage.meta.currentPage === 1) {
        return undefined
      }
      return currentPage.meta.currentPage - 1
    },
    placeholderData: (data) => data,
  })
